<template>
    <div 
        swipe 
        question 
        :data-drag-is-active="dragIsActive"
        :id="`question-${questionData.number}`" 
        @keyup.enter="submitQuestion" 
    >
        <questionheader :questionData="questionData" :showImage="false" />

        <div data-question-main>
            <div 
                data-draggable-image
                :data-selected-option="selectedOptionName"
                ref="draggable-image"
                :style="{
                    height: calcDraggableImageHeight()
                }"
            >
                <zoom-image
                    v-if="questionData.media.length > 0"
                    :src="mainImageUrl"
                    :id="`question-${questionData.number}_media-image`"
                    :alt="questionData.media[0].alt"
                    data-main-image
                    draggable="true"
                    @dragStart="onDragStart"
                    :data-object-fit="questionData.settings.imageObjectFit"
                />
                <div
                    v-else
                    @dragstart="onDragStart"
                    data-main-image
                    @touchstart="onDragStart"
                    draggable="true"
                >
                    <p>{{questionData.text}}</p>
                </div>
                <div 
                    :data-is-small-image="isSmallImage"
                    data-overlay
                    :style="{
                        width: imageWidth,
                        height: imageHeight
                    }"
                >
                </div>
            </div>
            <div data-answers>
                <div
                    data-answer
                >
                    <img 
                        :src="`${state.surveyInfo.domain}/v1/${negativeAnswer.media[0].url}`"
                        :id="`question-${negativeAnswer.number}_media-image`"
                        :alt="$store.getters.textResource(`rs_swipe_left_button_alt`)"
                        data-option-image
                        role="button"
                        @click="selectAnswer(0)"
                        @keydown.space="selectAnswer(0)"
                        @touchstart="selectAnswer(0)"
                        tabindex="0"
                        :disabled="questionData.isFrozen"
                        :data-checked="negativeAnswer.isChecked"
                    />
                    <answerdescription v-if="negativeAnswer.description !== ``" :answerData="negativeAnswer" :questionData="questionData" />
                </div>
                <div
                    v-if="neutralAnswer !== undefined"
                    data-answer
                >
                    <img 
                        :src="`${state.surveyInfo.domain}/v1/${neutralAnswer.media[0].url}`"
                        :id="`question-${neutralAnswer.number}_media-image`"
                        :alt="$store.getters.textResource(`rs_swipe_right_button_alt`)"
                        data-option-image
                        role="button"
                        @click="selectAnswer(2)"
                        @touchstart="selectAnswer(2)"
                        @keydown.space="selectAnswer(2)"
                        tabindex="0"
                        :disabled="questionData.isFrozen"
                        :data-checked="neutralAnswer.isChecked"
                    />
                    <answerdescription v-if="neutralAnswer.description !== ``" :answerData="neutralAnswer" :questionData="questionData" />
                </div>
                <div
                    data-answer
                >
                    <img 
                        :src="`${state.surveyInfo.domain}/v1/${positiveAnswer.media[0].url}`"
                        :id="`question-${positiveAnswer.number}_media-image`"
                        :alt="$store.getters.textResource(`rs_swipe_up_button_alt`)"
                        data-option-image
                        role="button"
                        @click="selectAnswer(1)"
                        @touchstart="selectAnswer(1)"
                        @keydown.space="selectAnswer(1)"
                        tabindex="0"
                        :disabled="questionData.isFrozen"
                        :data-checked="positiveAnswer.isChecked"
                    />
                    <answerdescription v-if="positiveAnswer.description !== ``" :answerData="positiveAnswer" :questionData="questionData" />
                </div>
            </div>
        </div>
        
        <validationpopup :questionValidation="questionValidation" />
        <questionfooter :questionData="questionData" />
    </div>
</template>

<script>
import mixins from "@/global/mixins";
import zoomImage from "@/components/ZoomImage";

export default {
    name: `swipe-question`,
    components: {
        zoomImage
    },
    data(){
        return {
            draggedClone: undefined,
            dragIsActive: false,
            imageHeight: `100%`,
            imageWidth: `100%`,
            isSmallImage: false,
            isImage: false,
            selectedOptionValue: ``
        }
    },
    mixins: [mixins.defaultQuestion],
    computed: {
        answersGiven() {
            return this.questionData.answers.filter(answer => answer.isChecked).length;
        },
        negativeAnswer(){
            return this.questionData.answers[0];
        },
        positiveAnswer(){
            return this.questionData.answers[1];
        },
        neutralAnswer(){
            return this.questionData.answers[2];
        },
        mainImageUrl(){
            return `${this.state.surveyInfo.domain}/v1/${this.questionData.media[0].url}`;
        },
        selectedOptionName(){
            const selectedAnswer = this.questionData.answers.find(a => a.isChecked);
            
            switch(selectedAnswer?.number) {
                case 1:
                    return `negative`;
                case 2:
                    return `positive`;
                case 3:
                    return `neutral`;
            }

            return false;
        }
    },
    methods: {
        selectAnswer(answerIndex) {
            this.questionData.answers.forEach(a => a.isChecked = false);
            
            this.questionData.answers[answerIndex].isChecked = true;
            
            this.$store.dispatch(`autoNext`, {
                questionData: this.questionData,
            });
        },
        calcDraggableImageHeight() {
            return `calc(100vh - ${300}px)`;
        },
        onDragStart(e){
            const elementToClone = e.target.offsetParent;
            const clone = elementToClone.cloneNode(true);
            const scale = window.visualViewport.scale;

            let initialLeft;
            let initialTop;
            let mouseOffsetLeft;
            let mouseOffsetTop;
            
            if(e.target.hasAttribute(`data-main-image`) === false) return;

            if(e.type === `touchstart`){
                e.preventDefault();

                const br = e.touches[0].target.getBoundingClientRect();
                
                initialLeft = ( e.target?.x || e.touches[0].target.x || e.touches[0].pageX) + window.scrollX
                initialTop = (e.target?.y || e.touches[0].target.y || e.touches[0].pageY) + window.scrollY / scale
               
                mouseOffsetLeft = e.touches[0].clientX - br.left;
                mouseOffsetTop = (e.touches[0].clientY - br.top) * scale;
            } else {
                initialLeft = e.clientX - e.offsetX + window.pageXOffset;
                initialTop = e.clientY - e.offsetY + window.pageYOffset;
                mouseOffsetLeft = e.offsetX;
                mouseOffsetTop = e.offsetY;
            }
            // hide the drag ghost
            if(e.dataTransfer){
                const img = new Image(0, 0);
                img.src = 'data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7',
                img.width = 0;
                img.height = 0;
                img.opacity = 0;
                img.style.visibility = "hidden";
                e.dataTransfer.setDragImage(img, -999999, -9999999);
            }
            
            this.dragIsActive = true;

            clone.id = `drag-clone`;
            document.body.appendChild(clone);

            this.draggedClone = {
                element: clone,
                left: initialLeft,
                top: initialTop,
                initialLeft,
                initialTop,
                mouseOffsetLeft,
                mouseOffsetTop
            }
            
            clone.style.top = `${this.draggedClone.top}px`;
            clone.style.left = `${this.draggedClone.left}px`;
            clone.style.width = `${elementToClone.offsetWidth}px`;
        },
        onDragOver(e) {
            if(this.draggedClone === undefined) return;

            let draggedLeft;
            let draggedTop;
            
            if(e.type === `touchmove`){
                const scale = window.visualViewport.scale;
                const centerOfScreenWidth = window.visualViewport.width / 2;
                const centerOfScreenHeight = window.visualViewport.height / 2;

                draggedLeft = e.touches[0].clientX - this.draggedClone.mouseOffsetLeft + window.visualViewport.pageLeft; 
                draggedTop = e.touches[0].clientY - this.draggedClone.mouseOffsetTop + window.visualViewport.pageTop; 

                if(e.touches[0].clientX < centerOfScreenWidth - 80 / scale) {
                    this.selectedOptionValue = `negative`;
                } else if(e.touches[0].clientX > centerOfScreenWidth + 80 / scale) {
                    this.selectedOptionValue = `positive`;
                } else if(this.neutralAnswer !== undefined && e.touches[0].clientY < centerOfScreenHeight) {
                    this.selectedOptionValue = `neutral`;
                } else {
                    this.selectedOptionValue = ``;
                }
            } else {
                e.preventDefault();
                draggedLeft = e.clientX - this.draggedClone.mouseOffsetLeft + window.pageXOffset;
                draggedTop = e.clientY - this.draggedClone.mouseOffsetTop + window.pageYOffset;

                if(draggedLeft < this.draggedClone.initialLeft - 80) {
                    this.selectedOptionValue = `negative`;
                } else if(draggedLeft > this.draggedClone.initialLeft + 80) {
                    this.selectedOptionValue = `positive`;
                } else if(this.neutralAnswer !== undefined && draggedTop < this.draggedClone.initialTop - 80) {
                    this.selectedOptionValue = `neutral`;
                } else {
                    this.selectedOptionValue = ``;
                }
            }

            this.draggedClone.element.style.top = `${draggedTop}px`;
            this.draggedClone.element.style.left = `${draggedLeft}px`;
            
            document.getElementById(`drag-clone`)?.setAttribute(`data-selected-option`, this.selectedOptionValue);
        },
        onDrop(e) {
            e.preventDefault();
            if(this.draggedClone === undefined) return;
            
            if(this.selectedOptionValue === `negative`) this.selectAnswer(0);
            else if(this.selectedOptionValue === `positive`) this.selectAnswer(1);
            else if(this.selectedOptionValue === `neutral`) this.selectAnswer(2);
        },
        onTouchEnd(e){
            if(e.target.hasAttribute(`data-button`) === true) return;
            
            this.onDrop(e);
            this.onDragEnd(e);
        },
        onDragEnd(){
            if(this.draggedClone === undefined) return;

            this.dragIsActive = false;
            this.draggedClone.element.remove();
            this.draggedClone = undefined;
        }
    },
    mounted(){
        document.addEventListener(`drop`, this.onDrop);
        document.addEventListener(`touchend`, this.onTouchEnd);
        document.addEventListener(`dragover`, this.onDragOver);
        document.addEventListener(`touchmove`, this.onDragOver);
        document.addEventListener(`dragend`, this.onDragEnd);

        if(this.questionData.media.length === 0 || this.questionData.settings.imageObjectFit !== `ScaleDown`) return;

        const _this = this;
        const img = new Image();

        img.onload = function() {
            _this.imageWidth = `${this.width}px`;
            _this.imageHeight = `${this.height}px`;
            
            if(this.width < 80 || this.height < 80) _this.isSmallImage = true;
        }
        img.src = this.mainImageUrl;
    },
    destroyed() {
        document.removeEventListener(`dragover`, this.onDragOver);
        document.removeEventListener(`touchmove`, this.onDragOver);
        document.removeEventListener(`drop`, this.onDrop);
        document.removeEventListener(`touchend`, this.onTouchEnd);
        document.removeEventListener(`dragend`, this.onDragEnd);
    }
};
</script>

<style lang="scss" scoped>
    [data-main-image] {
        border-radius: 30px;
        width: 100%;
        height: 100%;
        cursor: pointer;
        display: flex;
        justify-content: center;
        align-items: center;
        overflow: hidden;

        p {
            padding: 20px;
            pointer-events: none;
            text-align: center;
            max-height: 100%;
        }
    }

    [data-draggable-image] {
        display: flex;
        justify-content: center;
        align-items: center;
        background: rgba(255, 255, 255, 0);
        height: 100%;
        width: 100%;
        position: relative;

        [data-overlay] {
            content:"";
            position: absolute;
            border-radius: 30px;
            top: 0;
            left: 0;
            height: 100%;
            width: 100%;
            z-index: 999;
            pointer-events: none;
            transition: 0.5s;
            left: 50%;
            top: 50%;
            transform: translate(-50%, -50%);
            
            &[data-is-small-image] {
                border-radius: 0;
            }
        }
        
        &[data-selected-option="negative"] [data-overlay] {
            background: rgba(255, 0, 0, 0.4);
        }

        &[data-selected-option="positive"] [data-overlay] {
            background: rgba(91, 255, 15, 0.4);
        }

        &[data-selected-option="neutral"] [data-overlay] {
            background: rgba(255, 240, 36, 0.4);
        }
    }

    [data-answer] {
        display: flex;
        justify-content: center;    
        align-items: center;
        [data-tooltip] {
            margin-bottom: 70px;
            margin-left: -8px;
        }
    }

    [data-checked] {
        border-radius: 5px;
        border: 2px solid var(--primary-color);
    }

    #drag-clone {
        position: absolute;
        pointer-events: none;
        z-index: 11;
    }

    [data-drag-is-active] {
        [data-main-image] {
            opacity: 0;
        }

        [data-draggable-image]:after {
            transition: none;
            background: transparent;
        }
    }
    
    [data-object-fit="None"] {
        object-fit: none;
    }

    [data-object-fit="Contain"] {
        object-fit: contain;
    }

    [data-object-fit="ScaleDown"] {
        object-fit: scale-down;
    }

    [data-object-fit="Fill"] {
        object-fit: fill;
    }

    [data-object-fit="Cover"] {
        object-fit: cover;
    }

    [data-answers] {
        display: flex;
        margin-left: -15px;
        margin-right: -15px;

        [data-option-image] {
            max-width: 80px;
            margin: 15px;

            &[disabled] {
                pointer-events: none;
                filter: grayscale(1);
                opacity: 0.5;
            }
        }
    }
</style>